import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

type JsonLd = Record<string, unknown>;

interface SeoConfig {
  title: string;
  description: string;
  imagePath?: string;
  structuredData?: JsonLd[];
}

const SITE_URL = (import.meta.env.VITE_SITE_URL ?? 'https://adaptiveendurance.app').replace(/\/+$/, '');
const DEFAULT_IMAGE_PATH = '/android-chrome-512x512.png';
const SITE_NAME = 'Adaptive Endurance';

const INDEXABLE_PATHS = new Set([
  '/',
  '/features',
  '/about',
  '/pricing',
  '/roadmap',
  '/terms',
  '/privacy',
  '/cookie-policy',
]);

function normalizePath(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  const normalized = pathname.replace(/\/+$/, '');
  return normalized.length > 0 ? normalized : '/';
}

function buildCanonical(pathname: string): string {
  if (pathname === '/') return SITE_URL;
  return `${SITE_URL}${pathname}`;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function upsertCanonical(href: string): void {
  let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
}

function clearManagedJsonLd(): void {
  const scripts = document.head.querySelectorAll('script[type="application/ld+json"][data-seo-managed="true"]');
  scripts.forEach(script => script.remove());
}

function appendJsonLd(jsonLd: JsonLd): void {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-seo-managed', 'true');
  script.text = JSON.stringify(jsonLd);
  document.head.appendChild(script);
}

function getHomeStructuredData(): JsonLd[] {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: SITE_NAME,
      applicationCategory: 'HealthApplication',
      operatingSystem: 'Web',
      description:
        'Adaptive endurance training plans for runners and cyclists with science-based progression and dynamic schedule reflow.',
      url: SITE_URL,
      offers: [
        {
          '@type': 'Offer',
          name: 'Foundation',
          price: '0',
          priceCurrency: 'USD',
        },
        {
          '@type': 'Offer',
          name: 'Elite Engine',
          price: '19',
          priceCurrency: 'USD',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            unitText: 'MONTH',
          },
        },
      ],
      featureList: [
        'Adaptive training plans',
        'Dynamic session reflow',
        'Running and cycling support',
        'Intervals.icu integration',
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      email: 'support@adaptiveendurance.app',
      sameAs: ['https://www.strava.com/clubs/adaptive-endurance'],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Is it truly fully free to make a personalized plan?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. You can generate, view, and print a personalized training plan for free. Advanced sync and analytics features are paid.',
          },
        },
        {
          '@type': 'Question',
          name: 'What if I do not have a power meter?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Workouts support power, heart rate, and RPE so athletes can train without a power meter.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I switch goals mid-plan?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. You can update your goal and rebuild the roadmap when race plans change.',
          },
        },
      ],
    },
  ];
}

const SEO_CONFIG: Record<string, SeoConfig> = {
  '/': {
    title: 'Adaptive Endurance | Personalized Running & Cycling Training Plans',
    description:
      'Adaptive endurance training plans for running and cycling. Science-based progression, dynamic schedule reflow, and Intervals.icu sync.',
    structuredData: getHomeStructuredData(),
  },
  '/features': {
    title: 'Features | Adaptive Endurance',
    description:
      'Explore Adaptive Endurance features: deterministic progression, adaptive training architecture, and science-first endurance planning.',
  },
  '/pricing': {
    title: 'Pricing | Adaptive Endurance',
    description:
      'Transparent pricing for adaptive endurance coaching tools. Start free and upgrade for advanced analytics and synchronization.',
  },
  '/about': {
    title: 'About | Adaptive Endurance',
    description:
      'Learn how Adaptive Endurance combines athlete experience and engineering to deliver personalized, science-first coaching.',
  },
  '/roadmap': {
    title: 'Roadmap | Adaptive Endurance',
    description:
      'See the Adaptive Endurance roadmap and vote on upcoming product features for runners and cyclists.',
  },
  '/terms': {
    title: 'Terms of Service | Adaptive Endurance',
    description: 'Read the Adaptive Endurance Terms of Service and training safety disclaimers.',
  },
  '/privacy': {
    title: 'Privacy Policy | Adaptive Endurance',
    description: 'Read how Adaptive Endurance collects, stores, and protects your personal and training data.',
  },
  '/cookie-policy': {
    title: 'Cookie Policy | Adaptive Endurance',
    description: 'Learn how Adaptive Endurance uses essential, functionality, and analytics cookies.',
  },
};

const DEFAULT_SEO: SeoConfig = {
  title: 'Adaptive Endurance',
  description: 'Adaptive endurance training coach for running and cycling.',
};

export function RouteSeo() {
  const location = useLocation();

  useEffect(() => {
    const normalizedPath = normalizePath(location.pathname);
    const seo = SEO_CONFIG[normalizedPath] ?? DEFAULT_SEO;
    const canonical = buildCanonical(normalizedPath);
    const robots = INDEXABLE_PATHS.has(normalizedPath) ? 'index,follow' : 'noindex,nofollow';
    const imageUrl = `${SITE_URL}${seo.imagePath ?? DEFAULT_IMAGE_PATH}`;

    document.title = seo.title;
    upsertCanonical(canonical);

    upsertMeta('name', 'description', seo.description);
    upsertMeta('name', 'robots', robots);

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', imageUrl);
    upsertMeta('property', 'og:image:alt', SITE_NAME);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.description);
    upsertMeta('name', 'twitter:image', imageUrl);

    clearManagedJsonLd();
    (seo.structuredData ?? []).forEach(appendJsonLd);
  }, [location.pathname]);

  return null;
}
